import { useState } from 'react';
import { useToast } from '@shared/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { OrdersWithoutWeightApiAdapter } from '../adapters/OrdersWithoutWeightApiAdapter';
import { FilterConditionOutput } from '@components/ui/AdvancedFilter';
import { Filters } from '../models/Filters';
import type { AgGridTableProps } from '@shared/components/ui/AgGridTable';
import { mapFilterConditionToApiFilter } from './ordersWithoutWeightFilterConditionMapper';

type SortingConfig = NonNullable<AgGridTableProps['sorting']>;

function processFiltersForExport(
  filterConditions: FilterConditionOutput[],
  sorting: SortingConfig | null,
): {
  advanced_filters?: {
    filters: Array<{
      column: string;
      operator: string;
      value: string | string[] | number | number[] | boolean | null;
    }>;
    logic: 'AND' | 'OR';
  };
  sorting: {
    column: 'id' | 'ref' | 'order_created_at' | 'processed_at' | 'last_shipment_update';
    direction: 'asc' | 'desc';
  };
} {
  const advancedFilters = {
    filters: [] as Array<{
      column: string;
      operator: string;
      value: string | string[] | number | number[] | boolean | null;
    }>,
    logic: 'AND' as 'AND' | 'OR',
  };

  filterConditions.forEach((condition) => {
    advancedFilters.filters.push(mapFilterConditionToApiFilter(condition));
  });

  const sortingColumnMap: Record<string, string> = {
    id: 'id',
    ref: 'ref',
    created_at: 'order_created_at',
    processed_at: 'processed_at',
    last_shipment_update: 'last_shipment_update',
  };

  const sortingField = sorting?.field || 'id';
  const apiSortingColumn = sortingColumnMap[sortingField] || sortingColumnMap['id'];

  return {
    ...(advancedFilters.filters.length > 0
      ? {
          advanced_filters: advancedFilters,
        }
      : {}),
    sorting: {
      column: apiSortingColumn as 'id' | 'ref' | 'order_created_at' | 'processed_at' | 'last_shipment_update',
      direction: (sorting?.direction ?? 'asc') as 'asc' | 'desc',
    },
  };
}

export const useExportOrdersWithoutWeight = () => {
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation('ordersWithoutWeightModule');

  const exportOrdersWithoutWeight = async (
    filterConditions: FilterConditionOutput[],
    sorting: SortingConfig | null,
    visibleColumns?: string[],
  ) => {
    setIsExporting(true);

    try {
      const httpClient = createHttpClient(apiClientWH);
      const adapter = new OrdersWithoutWeightApiAdapter(httpClient);

      const processedFilters = processFiltersForExport(filterConditions, sorting);

      const exportFilters: Filters = {
        sorting: processedFilters.sorting,
        ...(processedFilters.advanced_filters && {
          advanced_filters: processedFilters.advanced_filters,
        }),
      };

      const response = await adapter.exportOrdersWithoutWeight(exportFilters, visibleColumns);

      if (response.url_file) {
        window.open(response.url_file, '_blank');

        toast({
          title: t('export.success.title', 'Exportación exitosa'),
          description: t('export.success.description', 'La descarga comenzará en una nueva pestaña.'),
          variant: 'default',
        });
      } else {
        throw new Error('No se recibió URL del archivo');
      }
    } catch (error: any) {
      toast({
        title: t('export.error.title', 'Error al exportar'),
        description: error.message || t('export.error.unexpected', 'Error inesperado al exportar. Inténtalo de nuevo.'),
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return {
    exportOrdersWithoutWeight,
    isExporting,
  };
};
